import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Banknote, Briefcase, Globe2, Network, ShieldCheck, Sparkles, Users, WalletCards } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusPill = (status) => {
  const map = {
    available: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
    requested: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
    approved: 'border-blue-400/20 bg-blue-500/10 text-blue-200',
    paid: 'border-primary/20 bg-primary/10 text-primary',
    on_hold: 'border-rose-400/20 bg-rose-500/10 text-rose-200',
    ready_placeholder: 'border-border-subtle bg-card/40 text-text-muted',
    connected: 'border-primary/20 bg-primary/10 text-primary',
  };
  return map[(status || '').toLowerCase()] || 'border-border-subtle bg-card/40 text-text-muted';
};

const officeTabs = [
  { id: 'overview', label: 'Resumen', icon: Sparkles },
  { id: 'clients', label: 'Clientes', icon: Briefcase },
  { id: 'commissions', label: 'Comisiones', icon: Banknote },
  { id: 'wallet', label: 'Wallet', icon: WalletCards },
  { id: 'social', label: 'Redes', icon: Globe2 },
  { id: 'network', label: 'Red', icon: Network },
  { id: 'legal', label: 'Legal', icon: ShieldCheck },
];

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

export const PartnerGrowthOffice = ({ office }) => {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState({ profile: false, social: false, lead: false, payout: false });
  const [topUpLoading, setTopUpLoading] = useState(null);
  const [profileForm, setProfileForm] = useState({
    display_name: '',
    legal_name: '',
    country: '',
    tax_id: '',
    iban: '',
    bank_account_holder: '',
    paypal_email: '',
    autopilot_mode: 'manual_review',
    accepted_terms: false,
    accepted_partner_contract: false,
    accepted_payout_policy: false,
    accepted_anti_spam_policy: false,
  });
  const [socialForm, setSocialForm] = useState({ provider: 'whatsapp', handle: '', status: 'ready_placeholder', notes: '' });
  const [leadForm, setLeadForm] = useState({ full_name: '', company_name: '', email: '', phone: '', source_channel: 'manual_referral', service_interest: '', estimated_value: '', priority: 'medium', follow_up_date: '', next_action: '', notes: '' });
  const [payoutForm, setPayoutForm] = useState({ payout_method: 'paypal', amount_eur: '', notes: '' });
  const [editingLeadId, setEditingLeadId] = useState(null);
  const plan = office?.plan || {};
  const metrics = office?.metrics || {};

  const headline = useMemo(() => {
    return office?.future_of_work?.headline || 'La IA no te quita tu empleo: te da una red, un sistema y tiempo de calidad.';
  }, [office]);

  useEffect(() => {
    if (!office) return;
    setProfileForm({
      display_name: office.profile?.display_name || '',
      legal_name: office.profile?.legal_name || '',
      country: office.profile?.country || '',
      tax_id: office.wallet?.payout_account?.tax_id || office.profile?.tax_id || '',
      iban: '',
      bank_account_holder: office.wallet?.payout_account?.bank_account_holder || '',
      paypal_email: '',
      autopilot_mode: office.profile?.autopilot_mode || 'manual_review',
      accepted_terms: Boolean((office.legal?.checks || []).find((item) => item.id === 'accepted_terms')?.done) || true,
      accepted_partner_contract: Boolean((office.legal?.checks || []).find((item) => item.id === 'partner_contract_signed')?.done),
      accepted_payout_policy: office.wallet?.payout_account?.payout_ready || false,
      accepted_anti_spam_policy: Boolean((office.legal?.checks || []).find((item) => item.id === 'anti_spam_acknowledged')?.done),
    });
    setSocialForm((prev) => ({ ...prev, provider: office.social_providers?.[0] || 'whatsapp' }));
    setPayoutForm((prev) => ({ ...prev, payout_method: office.wallet?.payout_account?.paypal_email_masked ? 'paypal' : 'bank_transfer' }));
  }, [office]);

  if (!office) return null;

  const notify = (message, type = 'success') => {
    if (typeof window !== 'undefined') {
      window.alert(message);
    }
  };

  const handleRequest = async (key, url, body, successMessage, resetFn) => {
    try {
      setLoading((prev) => ({ ...prev, [key]: true }));
      const res = await office.authFetch(url, body);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo completar la acción');
      if (resetFn) resetFn();
      notify(successMessage);
      await office.onRefresh?.();
    } catch (error) {
      notify(error.message || 'No se pudo completar la acción', 'error');
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    await handleRequest(
      'profile',
      `${API}/partner/office/profile`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...profileForm,
          tax_id: profileForm.tax_id || null,
          iban: profileForm.iban || null,
          bank_account_holder: profileForm.bank_account_holder || null,
          paypal_email: profileForm.paypal_email || null,
        }),
      },
      'Perfil partner actualizado correctamente',
      () => setProfileForm((prev) => ({ ...prev, iban: '', paypal_email: '' }))
    );
  };

  const submitSocial = async (e) => {
    e.preventDefault();
    await handleRequest(
      'social',
      `${API}/partner/office/social-connections`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(socialForm),
      },
      'Canal social actualizado correctamente',
      () => setSocialForm((prev) => ({ ...prev, handle: '', notes: '' }))
    );
  };

  const submitLead = async (e) => {
    e.preventDefault();
    const isEdit = Boolean(editingLeadId);
    await handleRequest(
      'lead',
      isEdit ? `${API}/partner/office/referral-leads/${editingLeadId}` : `${API}/partner/office/referral-leads`,
      {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...leadForm, estimated_value: Number(leadForm.estimated_value || 0) || null }),
      },
      isEdit ? 'Lead partner actualizado correctamente' : 'Lead partner creado correctamente',
      () => {
        setEditingLeadId(null);
        setLeadForm({ full_name: '', company_name: '', email: '', phone: '', source_channel: 'manual_referral', service_interest: '', estimated_value: '', priority: 'medium', follow_up_date: '', next_action: '', notes: '' });
      }
    );
  };

  const submitPayout = async (e) => {
    e.preventDefault();
    await handleRequest(
      'payout',
      `${API}/partner/office/payout-requests`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payoutForm, amount_eur: Number(payoutForm.amount_eur || 0) }),
      },
      'Solicitud de retiro enviada correctamente',
      () => setPayoutForm((prev) => ({ ...prev, amount_eur: '', notes: '' }))
    );
  };

  const launchTopUpCheckout = async (packId) => {
    try {
      setTopUpLoading(packId);
      const res = await office.authFetch(`${API}/credits/top-up-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: packId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo iniciar la recarga');
      window.location.href = data.url;
    } catch (error) {
      notify(error.message || 'No se pudo iniciar la recarga', 'error');
    } finally {
      setTopUpLoading(null);
    }
  };

  const startLeadEdit = (lead) => {
    setEditingLeadId(lead.id);
    setLeadForm({
      full_name: lead.full_name || '',
      company_name: lead.company_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      source_channel: lead.source_channel || 'manual_referral',
      service_interest: lead.service_interest || '',
      estimated_value: lead.estimated_value || '',
      priority: lead.priority || 'medium',
      follow_up_date: lead.follow_up_date ? String(lead.follow_up_date).slice(0, 16) : '',
      next_action: lead.next_action || '',
      notes: lead.notes || '',
    });
    setTab('clients');
  };

  const cancelLeadEdit = () => {
    setEditingLeadId(null);
    setLeadForm({ full_name: '', company_name: '', email: '', phone: '', source_channel: 'manual_referral', service_interest: '', estimated_value: '', priority: 'medium', follow_up_date: '', next_action: '', notes: '' });
  };

  return (
    <div className="space-y-6" data-testid="partner-growth-office">
      <div className="glass-card tracing-panel p-6 xl:p-8" data-testid="partner-growth-office-line-identity-card">
        <p className="editorial-kicker mb-4">Neuralia Partner</p>
        <h3 className="text-3xl font-semibold text-text-heading">Red comercial, afiliación inteligente y crecimiento recurrente</h3>
        <p className="mt-4 max-w-4xl text-base text-text-body">Este espacio es la línea <strong>Partner</strong> del ecosistema. Aquí se gestionan red, prospección, comisiones, wallet y activación comercial. La entrega de servicios a clientes finales vive en <strong>Neuralia Solutions</strong>.</p>
      </div>
      <div className="glass-card tracing-panel overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(197,168,128,0.18),transparent_28%),linear-gradient(180deg,rgba(17,17,17,0.98),rgba(10,10,10,0.94))]" data-testid="partner-growth-office-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-4" data-testid="partner-growth-office-kicker">Partner Office</p>
            <h3 className="text-3xl font-semibold text-text-heading" data-testid="partner-growth-office-heading">La IA no te quita tu empleo. Te da ingresos y tiempo de calidad para disfrutar la vida.</h3>
            <p className="mt-4 max-w-3xl text-base text-text-body" data-testid="partner-growth-office-subheading">Activa servicios inteligentes, construye ingresos recurrentes y deja que Neuralia produzca, supervise y mantenga la operación por ti.</p>
            <div className="mt-5 rounded-[22px] border border-primary/20 bg-primary/10 p-4 text-sm text-text-heading" data-testid="partner-growth-office-headline">{headline}</div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Saldo disponible" value={`€${Number(metrics.available_balance_eur || 0).toLocaleString('es-ES')}`} helper="comisiones listas para liquidar" testId="partner-growth-office-metric-balance" />
            <MetricCard label="Créditos restantes" value={`${metrics.credit_remaining || 0}`} helper={`${metrics.credit_used || 0}/${metrics.credit_limit || 0} usados`} testId="partner-growth-office-metric-credits" />
            <MetricCard label="Leads activos" value={`${metrics.active_referral_leads || 0}`} helper="clientes y referidos en seguimiento" testId="partner-growth-office-metric-leads" />
            <MetricCard label="Red conectada" value={`${office.network?.direct_partners || 0}`} helper="partners directos activos" testId="partner-growth-office-metric-network" />
          </div>
        </div>
        <div className="px-6 pb-6 xl:px-8" data-testid="partner-growth-office-credit-progress">
          <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-text-muted">
            <span>Uso de créditos</span>
            <span>{metrics.credit_utilization_percent || 0}%</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-background/60">
            <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${Math.min(metrics.credit_utilization_percent || 0, 100)}%` }}></div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4" data-testid="partner-growth-office-tab-switcher">
        <div className="flex flex-wrap gap-2">
          {officeTabs.map((item) => (
            <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`rounded-full px-4 py-2 text-sm transition ${tab === item.id ? 'bg-primary text-background' : 'border border-border-subtle bg-background/55 text-text-muted'}`} data-testid={`partner-growth-office-tab-${item.id}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && (
        <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]" data-testid="partner-growth-office-overview-view">
          <div className="space-y-6">
            {metrics.credit_alert?.show && (
              <div className={`rounded-[22px] border p-5 ${metrics.credit_alert.level === 'critical' ? 'border-rose-400/20 bg-rose-500/10' : 'border-amber-400/20 bg-amber-500/10'}`} data-testid="partner-growth-office-credit-alert">
                <p className="text-sm font-semibold text-text-heading">Aviso de créditos</p>
                <p className="mt-2 text-sm text-text-body">{metrics.credit_alert.message}</p>
              </div>
            )}
            <div className="glass-card p-6" data-testid="partner-growth-office-plan-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tu plan</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">{plan.label}</h4>
              <p className="mt-2 text-sm text-text-body">€{plan.monthly_price_eur}/mes · capacidad operativa privada · uso escalable de agentes según intensidad del plan.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(plan.highlights || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}
              </div>
            </div>
            <div className="glass-card p-6" data-testid="partner-growth-office-plan-catalog-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Planes activos</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Escalado por suscripción</h4>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {(office.plan_catalog || []).map((item) => (
                  <div key={item.id} className={`rounded-[20px] border p-4 ${item.id === plan.id ? 'border-primary/30 bg-primary/10' : 'border-border-subtle bg-background/55'}`} data-testid={`partner-growth-office-plan-option-${item.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-text-heading">{item.label}</p>
                      <span className="text-primary font-semibold">€{item.monthly_price_eur}</span>
                    </div>
                    <p className="mt-2 text-xs text-text-muted">{item.credit_limit} créditos · capacidad operativa y uso de agentes según plan</p>
                    <p className="mt-2 text-xs text-text-body">Comisión directa {item.direct_commission_range} · mantenimiento {item.maintenance_commission_range}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(item.highlights || []).map((tag) => <span key={tag} className="rounded-full border border-border-subtle bg-card/40 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{tag}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card p-6" data-testid="partner-growth-office-future-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Por qué ahora</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Ventana estratégica del mercado laboral</h4>
              <div className="mt-4 space-y-3">
                {(office.future_of_work?.signals || []).map((signal, index) => (
                  <div key={index} className="rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`partner-growth-office-signal-${index}`}>{signal}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="glass-card p-6" data-testid="partner-growth-office-maintenance-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Mantenimiento mensual</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Ingresos recurrentes por cliente</h4>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {(office.maintenance_packages || []).map((item) => (
                  <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-maintenance-${item.id}`}>
                    <p className="text-sm font-semibold text-text-heading">{item.label}</p>
                    <p className="mt-2 text-2xl font-bold text-primary">€{item.monthly_price_eur}/mes</p>
                    <p className="mt-2 text-sm text-text-body">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card p-6" data-testid="partner-growth-office-autopilot-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Piloto automático</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Automatización bajo control admin</h4>
              <div className="mt-4 space-y-3">
                {(office.autopilot_modules || []).map((item) => (
                  <div key={item.id} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`partner-growth-office-autopilot-${item.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-text-heading">{item.label}</p>
                      <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                    </div>
                    <p className="mt-2 text-sm text-text-body">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card p-6" data-testid="partner-growth-office-whatsapp-readiness-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">WhatsApp Cloud API</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Integración preparada</h4>
              <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body">
                <p>Estado: <span className="text-text-heading">{office.whatsapp_readiness?.status}</span></p>
                <p className="mt-2">Siguiente paso: <span className="text-text-heading">{office.whatsapp_readiness?.next_step}</span></p>
                {!!(office.whatsapp_readiness?.missing_credentials || []).length && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="partner-growth-office-whatsapp-missing-credentials">
                    {(office.whatsapp_readiness?.missing_credentials || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div className="glass-card p-6" data-testid="partner-growth-office-integrations-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Integraciones del ecosistema</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Lo que ya está y lo que falta activar</h4>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {[
                  { id: 'whatsapp', name: 'WhatsApp Cloud API', status: office.whatsapp_readiness?.status, helper: office.whatsapp_readiness?.next_step },
                  { id: 'ga4', name: 'GA4', status: process.env.REACT_APP_GA_MEASUREMENT_ID ? 'connected' : 'pending_credentials', helper: process.env.REACT_APP_GA_MEASUREMENT_ID ? `ID detectado: ${process.env.REACT_APP_GA_MEASUREMENT_ID}` : 'Pendiente Measurement ID' },
                  { id: 'meta-pixel', name: 'Meta Pixel', status: process.env.REACT_APP_META_PIXEL_ID ? 'connected' : 'pending_credentials', helper: process.env.REACT_APP_META_PIXEL_ID ? `ID detectado: ${process.env.REACT_APP_META_PIXEL_ID}` : 'Pendiente Pixel ID' },
                  { id: 'resend', name: 'Email transaccional', status: 'connected', helper: 'Infraestructura preparada para notificaciones y comunicaciones del sistema.' },
                ].map((item) => (
                  <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-integration-${item.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                      <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                    </div>
                    <p className="mt-3 text-sm text-text-body">{item.helper}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'clients' && (
        <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]" data-testid="partner-growth-office-clients-view">
          <form onSubmit={submitLead} className="glass-card p-6 space-y-3" data-testid="partner-growth-office-lead-form">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Alta rápida</p>
            <h4 className="text-xl font-semibold text-text-heading">{editingLeadId ? 'Editar lead partner' : 'Crear nuevo lead partner'}</h4>
            <input className="input-kinesthetic" placeholder="Nombre completo" value={leadForm.full_name} onChange={(e) => setLeadForm((prev) => ({ ...prev, full_name: e.target.value }))} data-testid="partner-growth-office-lead-name-input" />
            <input className="input-kinesthetic" placeholder="Empresa" value={leadForm.company_name} onChange={(e) => setLeadForm((prev) => ({ ...prev, company_name: e.target.value }))} data-testid="partner-growth-office-lead-company-input" />
            <div className="grid gap-3 md:grid-cols-2">
              <input className="input-kinesthetic" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="partner-growth-office-lead-email-input" />
              <input className="input-kinesthetic" placeholder="Teléfono" value={leadForm.phone} onChange={(e) => setLeadForm((prev) => ({ ...prev, phone: e.target.value }))} data-testid="partner-growth-office-lead-phone-input" />
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <input className="input-kinesthetic" placeholder="Canal de origen" value={leadForm.source_channel} onChange={(e) => setLeadForm((prev) => ({ ...prev, source_channel: e.target.value }))} data-testid="partner-growth-office-lead-source-input" />
              <input className="input-kinesthetic" placeholder="Valor estimado (€)" type="number" min="0" value={leadForm.estimated_value} onChange={(e) => setLeadForm((prev) => ({ ...prev, estimated_value: e.target.value }))} data-testid="partner-growth-office-lead-value-input" />
              <select className="input-kinesthetic" value={leadForm.priority} onChange={(e) => setLeadForm((prev) => ({ ...prev, priority: e.target.value }))} data-testid="partner-growth-office-lead-priority-select">
                {(office.lead_priorities || []).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </div>
            <input className="input-kinesthetic" placeholder="Servicio de interés" value={leadForm.service_interest} onChange={(e) => setLeadForm((prev) => ({ ...prev, service_interest: e.target.value }))} data-testid="partner-growth-office-lead-service-input" />
            <div className="grid gap-3 md:grid-cols-2">
              <input className="input-kinesthetic" type="datetime-local" value={leadForm.follow_up_date} onChange={(e) => setLeadForm((prev) => ({ ...prev, follow_up_date: e.target.value }))} data-testid="partner-growth-office-lead-follow-up-input" />
              <input className="input-kinesthetic" placeholder="Siguiente acción" value={leadForm.next_action} onChange={(e) => setLeadForm((prev) => ({ ...prev, next_action: e.target.value }))} data-testid="partner-growth-office-lead-next-action-input" />
            </div>
            <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Notas comerciales" value={leadForm.notes} onChange={(e) => setLeadForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="partner-growth-office-lead-notes-input" />
            <div className="flex flex-wrap gap-2">
              <button type="submit" className="btn-primary" disabled={loading.lead} data-testid="partner-growth-office-lead-submit-button">{loading.lead ? 'Guardando...' : editingLeadId ? 'Guardar lead' : 'Crear lead'}</button>
              {editingLeadId && <button type="button" className="btn-secondary" onClick={cancelLeadEdit} data-testid="partner-growth-office-lead-cancel-edit-button">Cancelar edición</button>}
            </div>
          </form>
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Clientes y referidos</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Pipeline comercial del Partner</h4>
            <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="partner-growth-office-crm-temperature-summary">
              <MetricCard label="Calientes" value={`${metrics.lead_temperature_breakdown?.hot || 0}`} helper="alta intención" testId="partner-growth-office-hot-leads-metric" />
              <MetricCard label="Tibios" value={`${metrics.lead_temperature_breakdown?.warm || 0}`} helper="requieren seguimiento" testId="partner-growth-office-warm-leads-metric" />
              <MetricCard label="Fríos" value={`${metrics.lead_temperature_breakdown?.cold || 0}`} helper="nurture o reactivación" testId="partner-growth-office-cold-leads-metric" />
            </div>
            <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid="partner-growth-office-overdue-followups-box">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Seguimientos vencidos</p>
              <div className="mt-3 space-y-2">
                {(office.crm?.overdue_followups || []).length === 0 && <p className="text-sm text-text-body">No tienes seguimientos vencidos ahora mismo.</p>}
                {(office.crm?.overdue_followups || []).map((lead) => <div key={lead.id} className="text-sm text-text-body" data-testid={`partner-growth-office-overdue-followup-${lead.id}`}>{lead.full_name} · {lead.next_action || 'Definir siguiente paso'}</div>)}
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {(office.referral_leads || []).map((lead) => (
                <div key={lead.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-lead-${lead.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{lead.full_name}</p>
                      <p className="mt-1 text-xs text-text-muted">{lead.company_name || 'Sin empresa'} · {lead.source_channel} · {lead.stage_label}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(lead.status)}`}>{lead.status}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">Score {lead.score}</span>
                    <span className="rounded-full border border-border-subtle bg-background/55 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{lead.temperature}</span>
                    <span className="rounded-full border border-border-subtle bg-background/55 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{lead.priority_label}</span>
                  </div>
                  <p className="mt-3 text-sm text-text-body">{lead.service_interest}</p>
                  <p className="mt-2 text-sm text-primary">€{Number(lead.estimated_value || 0).toLocaleString('es-ES')}</p>
                  <p className="mt-2 text-xs text-text-muted">Siguiente paso: {lead.next_step_recommendation}</p>
                  <p className="mt-2 text-xs text-text-muted">Follow-up: {lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleString('es-ES') : 'sin fecha'} {lead.follow_up_overdue ? '· vencido' : ''}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" className="btn-secondary text-xs" onClick={() => startLeadEdit(lead)} data-testid={`partner-growth-office-lead-edit-${lead.id}`}>Editar</button>
                  </div>
                  <div className="mt-3 space-y-2" data-testid={`partner-growth-office-lead-timeline-${lead.id}`}>
                    {(lead.timeline || []).slice(0, 3).map((item) => (
                      <div key={item.id} className="rounded-[16px] border border-border-subtle bg-background/55 p-3">
                        <p className="text-xs uppercase tracking-[0.14em] text-text-muted">{item.label}</p>
                        <p className="mt-1 text-sm text-text-body">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'commissions' && (
        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]" data-testid="partner-growth-office-commissions-view">
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Modelo de comisiones</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Escala por plan e ingresos recurrentes</h4>
            <div className="mt-4 rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body">
              <p>Venta directa: <span className="text-text-heading">{office.commission_model?.direct_sales}</span></p>
              <p className="mt-2">Mantenimiento mensual: <span className="text-text-heading">{office.commission_model?.maintenance}</span></p>
              <p className="mt-2">Red 3 niveles: <span className="text-text-heading">L1 {office.commission_model?.network_rates?.level_1} · L2 {office.commission_model?.network_rates?.level_2} · L3 {office.commission_model?.network_rates?.level_3}</span></p>
            </div>
          </div>
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Histórico</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Entradas de comisión</h4>
            <div className="mt-4 space-y-3">
              {(office.commission_entries || []).map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-commission-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.description}</p>
                      <p className="mt-1 text-xs text-text-muted">{item.kind} · {item.maintenance_package_id || 'sin paquete'}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                  </div>
                  <p className="mt-3 text-lg font-bold text-primary">€{Number(item.amount_eur || 0).toLocaleString('es-ES')}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'wallet' && (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]" data-testid="partner-growth-office-wallet-view">
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Wallet</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Cobro de comisiones</h4>
            <div className="mt-4 space-y-3 text-sm text-text-body">
              <p>IBAN: <span className="text-text-heading">{office.wallet?.payout_account?.iban_masked || 'pendiente'}</span></p>
              <p>PayPal: <span className="text-text-heading">{office.wallet?.payout_account?.paypal_email_masked || 'pendiente'}</span></p>
              <p>Retiro mínimo: <span className="text-text-heading">€{office.wallet?.minimum_payout_eur || 100}</span></p>
              <p>Saldo retirable: <span className="text-text-heading">€{Number(office.wallet?.withdrawable_balance_eur || 0).toLocaleString('es-ES')}</span></p>
              <p>En revisión: <span className="text-text-heading">€{Number(office.wallet?.pending_review_eur || 0).toLocaleString('es-ES')}</span></p>
            </div>
            <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body">{office.wallet?.retention_policy}</div>
            <form onSubmit={submitPayout} className="mt-4 space-y-3" data-testid="partner-growth-office-payout-form">
              <select className="input-kinesthetic" value={payoutForm.payout_method} onChange={(e) => setPayoutForm((prev) => ({ ...prev, payout_method: e.target.value }))} data-testid="partner-growth-office-payout-method-select">
                <option value="paypal">PayPal</option>
                <option value="bank_transfer">Transferencia bancaria</option>
              </select>
              <input className="input-kinesthetic" type="number" min="1" placeholder="Importe a retirar" value={payoutForm.amount_eur} onChange={(e) => setPayoutForm((prev) => ({ ...prev, amount_eur: e.target.value }))} data-testid="partner-growth-office-payout-amount-input" />
              <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas de retiro" value={payoutForm.notes} onChange={(e) => setPayoutForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="partner-growth-office-payout-notes-input" />
              <button type="submit" className="btn-primary" disabled={loading.payout} data-testid="partner-growth-office-payout-submit-button">{loading.payout ? 'Enviando...' : 'Solicitar retiro'}</button>
            </form>

            <div className="mt-6 rounded-[20px] border border-primary/20 bg-primary/10 p-4" data-testid="partner-growth-office-topup-box">
              <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Recarga privada</p>
              <p className="mt-2 text-sm text-text-body">Cuando te quede poca capacidad operativa, puedes recargar aquí mismo sin salir de tu panel.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {(office.credit_top_up_packs || []).map((pack) => (
                  <button key={pack.id} type="button" className="rounded-[18px] border border-primary/20 bg-background/60 p-4 text-left transition hover:-translate-y-1" onClick={() => launchTopUpCheckout(pack.id)} data-testid={`partner-growth-office-topup-${pack.id}`}>
                    <p className="text-sm font-semibold text-text-heading">{pack.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{pack.credits} de capacidad privada</p>
                    <p className="mt-3 text-lg font-bold text-primary">${pack.price}</p>
                    <p className="mt-2 text-xs text-text-body">{topUpLoading === pack.id ? 'Redirigiendo...' : pack.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid="partner-growth-office-topup-history-box">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Historial de recargas</p>
              <div className="mt-4 space-y-3">
                {(office.wallet?.credit_top_up_history || []).length === 0 && <p className="text-sm text-text-body">Todavía no hay recargas registradas en este wallet.</p>}
                {(office.wallet?.credit_top_up_history || []).map((item) => (
                  <div key={item.id} className="rounded-[18px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-topup-history-${item.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-text-heading">{item.package_name || 'Recarga privada'}</p>
                        <p className="mt-1 text-xs text-text-muted">{new Date(item.created_at).toLocaleString('es-ES')} · {item.credits} de capacidad privada</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.payment_status)}`}>{item.payment_status}</span>
                    </div>
                    <p className="mt-3 text-sm text-text-body">Importe: ${item.amount} {(item.currency || 'usd').toUpperCase()}</p>
                    {item.credits_applied && <p className="mt-2 text-xs text-primary">Saldo tras aplicación: {item.credits_balance_after}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Solicitudes</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Retiros y liquidaciones</h4>
            <div className="mt-4 space-y-3">
              {(office.wallet?.payout_requests || []).map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-payout-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.payout_method}</p>
                      <p className="mt-1 text-xs text-text-muted">{item.notes || 'Solicitud de retiro'}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                  </div>
                  <p className="mt-3 text-lg font-bold text-primary">€{Number(item.amount_eur || 0).toLocaleString('es-ES')}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'social' && (
        <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]" data-testid="partner-growth-office-social-view">
          <form onSubmit={submitSocial} className="glass-card p-6 space-y-3" data-testid="partner-growth-office-social-form">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Conectar canal</p>
            <h4 className="text-xl font-semibold text-text-heading">Estado de prospección por red</h4>
            <select className="input-kinesthetic" value={socialForm.provider} onChange={(e) => setSocialForm((prev) => ({ ...prev, provider: e.target.value }))} data-testid="partner-growth-office-social-provider-select">
              {(office.social_providers || []).map((provider) => <option key={provider} value={provider}>{provider}</option>)}
            </select>
            <input className="input-kinesthetic" placeholder="Handle / usuario" value={socialForm.handle} onChange={(e) => setSocialForm((prev) => ({ ...prev, handle: e.target.value }))} data-testid="partner-growth-office-social-handle-input" />
            <select className="input-kinesthetic" value={socialForm.status} onChange={(e) => setSocialForm((prev) => ({ ...prev, status: e.target.value }))} data-testid="partner-growth-office-social-status-select">
              <option value="ready_placeholder">Preparado</option>
              <option value="connected">Conectado</option>
              <option value="pending_credentials">Pendiente credenciales</option>
              <option value="disconnected">Desconectado</option>
            </select>
            <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Notas de uso o próximos pasos" value={socialForm.notes} onChange={(e) => setSocialForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="partner-growth-office-social-notes-input" />
            <button type="submit" className="btn-primary" disabled={loading.social} data-testid="partner-growth-office-social-submit-button">{loading.social ? 'Guardando...' : 'Guardar canal'}</button>
          </form>
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Redes conectadas</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Canales disponibles para prospección</h4>
            <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid="partner-growth-office-social-playbooks-box">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Playbooks</p>
              <div className="mt-3 space-y-2">
                {(office.social_playbooks || []).map((item) => (
                  <div key={item.provider} className="text-sm text-text-body" data-testid={`partner-growth-office-social-playbook-${item.provider}`}>
                    <span className="text-text-heading">{item.provider}:</span> {item.helper}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-2">
              {(office.social_connections || []).map((item) => (
                <div key={item.id || item.provider} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`partner-growth-office-social-${item.provider}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.provider}</p>
                      <p className="mt-1 text-xs text-text-muted">{item.handle || 'Preparado para conexión real'}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(item.status)}`}>{item.status}</span>
                  </div>
                  <p className="mt-3 text-sm text-text-body">{item.notes}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'network' && (
        <div className="space-y-6" data-testid="partner-growth-office-network-view">
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Red</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Referidos y expansión</h4>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <MetricCard label="Nivel 1" value={office.network?.level_1 || 0} helper="partners directos" testId="partner-growth-office-network-level-1" />
              <MetricCard label="Nivel 2" value={office.network?.level_2 || 0} helper="expansión secundaria" testId="partner-growth-office-network-level-2" />
              <MetricCard label="Nivel 3" value={office.network?.level_3 || 0} helper="red terciaria" testId="partner-growth-office-network-level-3" />
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-3" data-testid="partner-growth-office-network-rollup">
              <MetricCard label="MRR red" value={`€${Number(office.network?.rollup?.total_monthly_revenue_eur || 0).toLocaleString('es-ES')}`} helper="ingreso mensual agregado" testId="partner-growth-office-network-mrr" />
              <MetricCard label="Pipeline red" value={`€${Number(office.network?.rollup?.total_pipeline_value_eur || 0).toLocaleString('es-ES')}`} helper="valor comercial total" testId="partner-growth-office-network-pipeline" />
              <MetricCard label="Clientes activos" value={`${office.network?.rollup?.active_clients || 0}`} helper="cuentas vivas en la red" testId="partner-growth-office-network-active-clients" />
            </div>
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            {(office.network?.nodes || []).map((item) => (
              <div key={item.id} className="glass-card p-5" data-testid={`partner-growth-office-downline-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.email || 'sin email'}</p>
                  </div>
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">L{item.level}</span>
                </div>
                <p className="mt-3 text-xs text-text-muted">{item.country || 'Sin país'} · {item.tier_label}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                  <div>
                    <p className="text-text-muted">MRR</p>
                    <p className="mt-1 font-semibold text-text-heading">€{Number(item.monthly_revenue_eur || 0).toLocaleString('es-ES')}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Pipeline</p>
                    <p className="mt-1 font-semibold text-text-heading">€{Number(item.pipeline_value_eur || 0).toLocaleString('es-ES')}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Clientes</p>
                    <p className="mt-1 font-semibold text-text-heading">{item.active_clients}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Leads cualificados</p>
                    <p className="mt-1 font-semibold text-text-heading">{item.qualified_leads}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'legal' && (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]" data-testid="partner-growth-office-legal-view">
          <form onSubmit={submitProfile} className="glass-card p-6 space-y-3" data-testid="partner-growth-office-profile-form">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Legal</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Perfil legal y de cobro</h4>
            <div className="grid gap-3 md:grid-cols-2">
              <input className="input-kinesthetic" placeholder="Nombre visible" value={profileForm.display_name} onChange={(e) => setProfileForm((prev) => ({ ...prev, display_name: e.target.value }))} data-testid="partner-growth-office-profile-display-name-input" />
              <input className="input-kinesthetic" placeholder="Razón social / nombre legal" value={profileForm.legal_name} onChange={(e) => setProfileForm((prev) => ({ ...prev, legal_name: e.target.value }))} data-testid="partner-growth-office-profile-legal-name-input" />
              <input className="input-kinesthetic" placeholder="País principal" value={profileForm.country} onChange={(e) => setProfileForm((prev) => ({ ...prev, country: e.target.value }))} data-testid="partner-growth-office-profile-country-input" />
              <input className="input-kinesthetic" placeholder="NIF / VAT / Tax ID" value={profileForm.tax_id} onChange={(e) => setProfileForm((prev) => ({ ...prev, tax_id: e.target.value }))} data-testid="partner-growth-office-profile-tax-id-input" />
              <input className="input-kinesthetic" placeholder="Titular bancario" value={profileForm.bank_account_holder} onChange={(e) => setProfileForm((prev) => ({ ...prev, bank_account_holder: e.target.value }))} data-testid="partner-growth-office-profile-bank-holder-input" />
              <select className="input-kinesthetic" value={profileForm.autopilot_mode} onChange={(e) => setProfileForm((prev) => ({ ...prev, autopilot_mode: e.target.value }))} data-testid="partner-growth-office-profile-autopilot-select">
                <option value="manual_review">Manual review</option>
                <option value="supervised_autopilot">Supervised autopilot</option>
                <option value="hybrid_growth">Hybrid growth</option>
              </select>
            </div>
            <input className="input-kinesthetic" placeholder="IBAN (opcional, solo si lo quieres actualizar)" value={profileForm.iban} onChange={(e) => setProfileForm((prev) => ({ ...prev, iban: e.target.value }))} data-testid="partner-growth-office-profile-iban-input" />
            <input className="input-kinesthetic" placeholder="PayPal email (opcional, solo si lo quieres actualizar)" value={profileForm.paypal_email} onChange={(e) => setProfileForm((prev) => ({ ...prev, paypal_email: e.target.value }))} data-testid="partner-growth-office-profile-paypal-input" />
            <div className="space-y-3 rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="partner-growth-office-profile-consent-box">
              <label className="flex items-center gap-3 text-sm text-text-body"><input type="checkbox" checked={profileForm.accepted_terms} onChange={(e) => setProfileForm((prev) => ({ ...prev, accepted_terms: e.target.checked }))} data-testid="partner-growth-office-profile-accepted-terms" /> Acepto los términos operativos del panel</label>
              <label className="flex items-center gap-3 text-sm text-text-body"><input type="checkbox" checked={profileForm.accepted_partner_contract} onChange={(e) => setProfileForm((prev) => ({ ...prev, accepted_partner_contract: e.target.checked }))} data-testid="partner-growth-office-profile-accepted-contract" /> Confirmo el contrato partner</label>
              <label className="flex items-center gap-3 text-sm text-text-body"><input type="checkbox" checked={profileForm.accepted_payout_policy} onChange={(e) => setProfileForm((prev) => ({ ...prev, accepted_payout_policy: e.target.checked }))} data-testid="partner-growth-office-profile-accepted-payout-policy" /> Confirmo la política de cobros y retenciones</label>
              <label className="flex items-center gap-3 text-sm text-text-body"><input type="checkbox" checked={profileForm.accepted_anti_spam_policy} onChange={(e) => setProfileForm((prev) => ({ ...prev, accepted_anti_spam_policy: e.target.checked }))} data-testid="partner-growth-office-profile-accepted-anti-spam" /> Confirmo la política anti-spam y consentimiento comercial</label>
            </div>
            <button type="submit" className="btn-primary" disabled={loading.profile} data-testid="partner-growth-office-profile-submit-button">{loading.profile ? 'Guardando...' : 'Guardar perfil partner'}</button>
          </form>
          <div className="glass-card p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Checklist de cumplimiento</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Estado legal y operativo</h4>
            <div className="mt-4 space-y-3">
              {(office.legal?.checks || []).map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 flex items-center justify-between gap-3" data-testid={`partner-growth-office-legal-check-${item.id}`}>
                  <span className="text-sm text-text-heading">{item.label}</span>
                  <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${item.done ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>{item.done ? 'ok' : 'pendiente'}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Contrato Partner</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Principios de operación</h4>
            <p className="mt-4 text-sm text-text-body">{office.legal?.contract_summary}</p>
            <div className="mt-4 space-y-3">
              {(office.legal?.compliance_notes || []).map((item, index) => (
                <div key={index} className="rounded-[20px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`partner-growth-office-legal-note-${index}`}>{item}</div>
              ))}
            </div>
            <div className="mt-6 rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid="partner-growth-office-whatsapp-activation-steps">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Guía de activación</p>
              <div className="mt-3 space-y-2 text-sm text-text-body">
                {(office.whatsapp_readiness?.planned_capabilities || []).map((item, index) => (
                  <div key={index}>• {item}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerGrowthOffice;